/**
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║                  🚦 PRE-PUBLISH CHECKLIST — READ BEFORE ADDING A ROUTE 🚦   ║
 * ╠══════════════════════════════════════════════════════════════════════════════╣
 * ║                                                                              ║
 * ║  Every new public route MUST satisfy all three requirements below.           ║
 * ║  Missing any one of them will cause a failed pre-publish scan.               ║
 * ║                                                                              ║
 * ║  ① ERROR BOUNDARY                                                            ║
 * ║     Wrap every route element in <ErrorBoundary>:                             ║
 * ║     ✅  { path: '/my-page', element: <ErrorBoundary><MyPage /></ErrorBoundary> } ║
 * ║     ❌  { path: '/my-page', element: <MyPage /> }                            ║
 * ║                                                                              ║
 * ║  ② useSEOHead()                                                              ║
 * ║     Call useSEOHead() at the top of every page component with a             ║
 * ║     UNIQUE title, description, and canonicalUrl:                             ║
 * ║     ✅  useSEOHead({ title: '...', description: '...', canonicalUrl: '...' })║
 * ║     ❌  No useSEOHead() call → page has no title/meta/canonical              ║
 * ║                                                                              ║
 * ║  ③ data-readdy-form (forms only)                                             ║
 * ║     Every <form> that submits data MUST have the attribute:                  ║
 * ║     ✅  <form data-readdy-form id="unique-form-id" ...>                      ║
 * ║     ❌  <form ...>  ← missing attribute, form won't be tracked               ║
 * ║     Also required on every form:                                             ║
 * ║       • Unique id attribute (one-to-one with submit URL)                     ║
 * ║       • name attribute on every input/select/textarea                        ║
 * ║       • email field must have name="email"                                   ║
 * ║       • textarea must have maxLength={500}                                   ║
 * ║       • Zod schema passed to useFormSubmit()                                 ║
 * ║       • useToast() for success/error feedback (no alert())                   ║
 * ║                                                                              ║
 * ╠══════════════════════════════════════════════════════════════════════════════╣
 * ║  QUICK CHECKLIST (copy into your PR description):                            ║
 * ║                                                                              ║
 * ║  [ ] Route wrapped in <ErrorBoundary>                                        ║
 * ║  [ ] Page calls useSEOHead() with unique title + description + canonicalUrl  ║
 * ║  [ ] All forms have data-readdy-form + unique id                             ║
 * ║  [ ] All form fields have name attribute                                     ║
 * ║  [ ] Email fields use name="email"                                           ║
 * ║  [ ] Textareas have maxLength={500}                                          ║
 * ║  [ ] Forms use Zod schema via useFormSubmit()                                ║
 * ║  [ ] Forms use useToast() for feedback                                       ║
 * ║                                                                              ║
 * ╠══════════════════════════════════════════════════════════════════════════════╣
 * ║  LAYOUT ARCHITECTURE                                                         ║
 * ║                                                                              ║
 * ║  Layout (nav + footer) is applied ONCE via the parent route in App.tsx.      ║
 * ║  Do NOT import Layout inside individual page files.                          ║
 * ║                                                                              ║
 * ║  ❌ NEVER:  import Layout from '...'; return <Layout><div/></Layout>;        ║
 * ║  ✅ ALWAYS: return <div>content</div>;  (Layout wraps it here)               ║
 * ║                                                                              ║
 * ║  See src/pages/_template.tsx for the correct page structure.                 ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 */

import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import AdminLayout from '../components/layout/AdminLayout';
import ErrorBoundary, { retryLazy } from '../components/base/ErrorBoundary';
import { AdminAuthProvider } from '../contexts/AdminAuthContext';

// Route-level error boundary wrapper
const RouteErrorBoundary = ({ children }: { children: React.ReactNode }) => (
  <ErrorBoundary>{children}</ErrorBoundary>
);

// Lazy load with prefetch capability AND automatic retry on chunk failure
const lazyWithPrefetch = (importFn: () => Promise<any>) => {
  const Component = retryLazy(importFn, 3, 800);
  // @ts-expect-error - Add prefetch method for route prefetching
  Component.preload = importFn;
  return Component;
};

// ── Critical pages (preload immediately) ──────────────────────────────────
const HomePage = lazyWithPrefetch(() => import('../pages/home/page'));
const InventoryPage = lazyWithPrefetch(() => import('../pages/inventory/page'));
const VehicleDetailPage = lazyWithPrefetch(() => import('../pages/vehicle-detail/page'));

// ── High-priority pages (load on hover/interaction) ──────────────────────
const ContactPage = lazyWithPrefetch(() => import('../pages/contact/page'));
const FinancingPage = lazyWithPrefetch(() => import('../pages/financing/page'));
const TradeInPage = lazyWithPrefetch(() => import('../pages/trade-in/page'));
const SellYourCarPage = lazyWithPrefetch(() => import('../pages/sell-your-car/page'));
const ReviewsPage = lazyWithPrefetch(() => import('../pages/reviews/page'));
const BrokerServicesPage = lazyWithPrefetch(() => import('../pages/broker-services/page'));

// ── Secondary pages (lazy load on demand) ─────────────────────────────────
const ApplyForFinancingPage = lazyWithPrefetch(() => import('../pages/apply-for-financing/page'));
const FinancingApprovalPage = lazyWithPrefetch(() => import('../pages/financing-approval/page'));
const WriteAReviewPage = lazyWithPrefetch(() => import('../pages/write-a-review/page'));
const BlogPage = lazyWithPrefetch(() => import('../pages/blog/page'));
const BlogArticlePage = lazyWithPrefetch(() => import('../pages/blog-article/page'));
const BlogNewsletterPage = lazyWithPrefetch(() => import('../pages/blog/newsletter/page'));
const VlogPage = lazyWithPrefetch(() => import('../pages/vlog/page'));
const ServiceAreasPage = lazyWithPrefetch(() => import('../pages/service-areas/page'));
const AstoriaPage = lazyWithPrefetch(() => import('../pages/service-areas/astoria/page'));
const SeasidePage = lazyWithPrefetch(() => import('../pages/service-areas/seaside/page'));
const WarrentonPage = lazyWithPrefetch(() => import('../pages/service-areas/warrenton/page'));
const PortlandPage = lazyWithPrefetch(() => import('../pages/service-areas/portland/page'));
const FaqPage = lazyWithPrefetch(() => import('../pages/faq/page'));
const ComparePage = lazyWithPrefetch(() => import('../pages/compare/page'));
const CompetitorPage = lazyWithPrefetch(() => import('../pages/competitor/page'));
const VsAstoriaFordPage = lazyWithPrefetch(() => import('../pages/competitor/vs-astoria-ford/page'));
const VsLumsToyotaPage = lazyWithPrefetch(() => import('../pages/competitor/vs-lums-toyota/page'));
const VsOceancrestChevroletPage = lazyWithPrefetch(() => import('../pages/competitor/vs-oceancrest-chevrolet/page'));
const VsWarrentonKiaPage = lazyWithPrefetch(() => import('../pages/competitor/vs-warrenton-kia/page'));
const BrokerServicesBendPage = lazyWithPrefetch(() => import('../pages/broker-services/bend/page'));
const BrokerServicesEugenePage = lazyWithPrefetch(() => import('../pages/broker-services/eugene/page'));
const BrokerServicesMedfordPage = lazyWithPrefetch(() => import('../pages/broker-services/medford/page'));
const BrokerServicesPortlandPage = lazyWithPrefetch(() => import('../pages/broker-services/portland/page'));
const BrokerServicesSeattlePage = lazyWithPrefetch(() => import('../pages/broker-services/seattle/page'));
const BrokerServicesWhyBrokerPage = lazyWithPrefetch(() => import('../pages/broker-services/why-broker/page'));
const RequestAVehiclePage = lazyWithPrefetch(() => import('../pages/broker-services/request-a-vehicle/page'));
const WhyUseABrokerPage = lazyWithPrefetch(() => import('../pages/why-use-a-broker/page'));
const CommunityImpactPage = lazyWithPrefetch(() => import('../pages/community-impact/page'));
const SoldVehiclesPage = lazyWithPrefetch(() => import('../pages/sold-vehicles/page'));
const SitemapPage = lazyWithPrefetch(() => import('../pages/sitemap/page'));
const ThankYouPage = lazyWithPrefetch(() => import('../pages/thank-you/page'));
const ThankYouFinancingPage = lazyWithPrefetch(() => import('../pages/thank-you/financing'));
const ThankYouReferralPage = lazyWithPrefetch(() => import('../pages/thank-you/referral'));
const ThankYouTradeInPage = lazyWithPrefetch(() => import('../pages/thank-you/trade-in'));
const ThankYouDogTagReferralPage = lazyWithPrefetch(() => import('../pages/thank-you/dog-tag-referral'));
const ThankYouCheckAvailabilityPage = lazyWithPrefetch(() => import('../pages/thank-you/check-availability'));
const ThankYouSellYourCarPage = lazyWithPrefetch(() => import('../pages/thank-you/sell-your-car'));
const ThankYouDealRadarPage = lazyWithPrefetch(() => import('../pages/thank-you/deal-radar'));
const ThankYouMilitaryPage = lazyWithPrefetch(() => import('../pages/thank-you/military'));
const VideosPage = lazyWithPrefetch(() => import('../pages/videos/page'));
const ConsignmentPage = lazyWithPrefetch(() => import('../pages/consignment/page'));
const CarsUnder10KPage = lazyWithPrefetch(() => import('../pages/cars-under-10k/page'));
const CarsUnder30KPage = lazyWithPrefetch(() => import('../pages/cars-under-30k/page'));
const HowItWorksPage = lazyWithPrefetch(() => import('../pages/how-it-works/page'));
const CustomerStoriesPage = lazyWithPrefetch(() => import('../pages/customer-stories/page'));
const CustomerStoriesDetailPage = lazyWithPrefetch(() => import('../pages/customer-stories/detail/page'));
const UsedCarsSWWashingtonPage = lazyWithPrefetch(() => import('../pages/used-cars-sw-washington/page'));
const UsedCarsAstoriaOregonPage = lazyWithPrefetch(() => import('../pages/used-cars-astoria-oregon/page'));
const DealRadarPage = lazyWithPrefetch(() => import('../pages/deal-radar/page'));
const PrivacyPolicyPage = lazyWithPrefetch(() => import('../pages/privacy-policy/page'));
const TermsOfServicePage = lazyWithPrefetch(() => import('../pages/terms-of-service/page'));
const ReferralPage = lazyWithPrefetch(() => import('../pages/referral/page'));
const NotFoundPage = lazyWithPrefetch(() => import('../pages/NotFound'));
const VehicleStickerPage = lazyWithPrefetch(() => import('../pages/vehicle-sticker/page'));
const UsedCarsSeasideOregonPage = lazyWithPrefetch(() => import('../pages/used-cars-seaside-oregon/page'));
const UsedCarsTillamookOregonPage = lazyWithPrefetch(() => import('../pages/used-cars-tillamook-oregon/page'));
const UsedCarsLongviewWashingtonPage = lazyWithPrefetch(() => import('../pages/used-cars-longview-washington/page'));
const UsedCarsIlwacoWashingtonPage = lazyWithPrefetch(() => import('../pages/used-cars-ilwaco-washington/page'));
const UsedCarsPortlandOregonPage = lazyWithPrefetch(() => import('../pages/used-cars-portland-oregon/page'));
const UsedCarsWarrentonOregonPage = lazyWithPrefetch(() => import('../pages/used-cars-warrenton-oregon/page'));
const BestUsedCarsUnder15kPage = lazyWithPrefetch(() => import('../pages/best-used-cars-under-15k/page'));
const BestUsedSUVsUnder20kPage = lazyWithPrefetch(() => import('../pages/best-used-suvs-under-20k/page'));
const BestFamilyCarsOregonPage = lazyWithPrefetch(() => import('../pages/best-family-cars-oregon/page'));
const BestFirstTimeBuyerCarsPage = lazyWithPrefetch(() => import('../pages/best-first-time-buyer-cars/page'));
const FirstTimeBuyerGuidePage = lazyWithPrefetch(() => import('../pages/first-time-buyer-guide/page'));
const WhyTheDogsPage = lazyWithPrefetch(() => import('../pages/why-the-dogs/page'));
const AboutTheOwnerPage = lazyWithPrefetch(() => import('../pages/about-the-owner/page'));
const MilitaryDiscountPage = lazyWithPrefetch(() => import('../pages/military-discount/page'));
const RockhoundDifferencePage = lazyWithPrefetch(() => import('../pages/rockhound-difference/page'));
const CannonBeachPage = lazyWithPrefetch(() => import('../pages/service-areas/cannon-beach/page'));
const GearhartPage = lazyWithPrefetch(() => import('../pages/service-areas/gearhart/page'));
const IlwacoPage = lazyWithPrefetch(() => import('../pages/service-areas/ilwaco/page'));
const TillamookServicePage = lazyWithPrefetch(() => import('../pages/service-areas/tillamook/page'));
const LongBeachWAPage = lazyWithPrefetch(() => import('../pages/service-areas/long-beach-wa/page'));
const ClatsopCountyPage = lazyWithPrefetch(() => import('../pages/service-areas/clatsop-county/page'));
const FISurvivalChecklistPage = lazyWithPrefetch(() => import('../pages/fi-survival-checklist/page'));
const About2BaldGuysPage = lazyWithPrefetch(() => import('../pages/about-2-bald-guys/page'));
const CompareVehiclesPage = lazyWithPrefetch(() => import('../pages/compare-vehicles/page'));

// ── Admin pages (lazy load, not critical for public site) ────────────────
const AdminDashboardPage = lazyWithPrefetch(() => import('../pages/admin/dashboard/page'));
const AdminInventoryPage = lazyWithPrefetch(() => import('../pages/admin/inventory/page'));
const AdminImagesPage = lazyWithPrefetch(() => import('../pages/admin/images/page'));
const AdminWindowStickerPage = lazyWithPrefetch(() => import('../pages/admin/window-sticker/page'));
const AdminReviewsPage = lazyWithPrefetch(() => import('../pages/admin/reviews/page'));
const AdminReviewsAnalyticsPage = lazyWithPrefetch(() => import('../pages/admin/reviews/analytics/page'));
const AdminContentPage = lazyWithPrefetch(() => import('../pages/admin/content/page'));
const AdminFeedsPage = lazyWithPrefetch(() => import('../pages/admin/feeds/page'));
const AdminDmsPage = lazyWithPrefetch(() => import('../pages/admin/dms/page'));
const AdminStickerAnalyticsPage = lazyWithPrefetch(() => import('../pages/admin/sticker-analytics/page'));
const AdminPagePreviewPage = lazyWithPrefetch(() => import('../pages/admin/page-preview/page'));
const AdminFaviconPage = lazyWithPrefetch(() => import('../pages/admin/favicon/page'));
const AdminPageHealthPage = lazyWithPrefetch(() => import('../pages/admin/page-health/page'));
const AdminReferralsPage = lazyWithPrefetch(() => import('../pages/admin/referrals/page'));
const AdminFinancingPage = lazyWithPrefetch(() => import('../pages/admin/financing/page'));
const AdminSetupPage = lazyWithPrefetch(() => import('../pages/admin/setup/page'));
const AdminSecurityTestPage = lazyWithPrefetch(() => import('../pages/admin/security-test/page'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <Layout />,
    errorElement: <ErrorBoundary />,
    children: [
      { index: true, element: <RouteErrorBoundary><HomePage /></RouteErrorBoundary> },
      { path: 'inventory', element: <RouteErrorBoundary><InventoryPage /></RouteErrorBoundary> },
      { path: 'inventory/:id', element: <RouteErrorBoundary><VehicleDetailPage /></RouteErrorBoundary> },
      { path: 'contact', element: <RouteErrorBoundary><ContactPage /></RouteErrorBoundary> },
      { path: 'about-2-bald-guys', element: <RouteErrorBoundary><About2BaldGuysPage /></RouteErrorBoundary> },
      { path: 'financing', element: <RouteErrorBoundary><FinancingPage /></RouteErrorBoundary> },
      { path: 'apply-for-financing', element: <RouteErrorBoundary><ApplyForFinancingPage /></RouteErrorBoundary> },
      { path: 'financing-approval', element: <RouteErrorBoundary><FinancingApprovalPage /></RouteErrorBoundary> },
      { path: 'trade-in', element: <RouteErrorBoundary><TradeInPage /></RouteErrorBoundary> },
      { path: 'sell-your-car', element: <RouteErrorBoundary><SellYourCarPage /></RouteErrorBoundary> },
      { path: 'reviews', element: <RouteErrorBoundary><ReviewsPage /></RouteErrorBoundary> },
      { path: 'write-a-review', element: <RouteErrorBoundary><WriteAReviewPage /></RouteErrorBoundary> },
      { path: 'blog', element: <RouteErrorBoundary><BlogPage /></RouteErrorBoundary> },
      { path: 'blog/newsletter', element: <RouteErrorBoundary><BlogNewsletterPage /></RouteErrorBoundary> },
      { path: 'blog/:slug', element: <RouteErrorBoundary><BlogArticlePage /></RouteErrorBoundary> },
      { path: 'vlog', element: <RouteErrorBoundary><VlogPage /></RouteErrorBoundary> },
      { path: 'sticker/:id', element: <RouteErrorBoundary><VehicleStickerPage /></RouteErrorBoundary> },
      { path: 'cars-under-10k', element: <RouteErrorBoundary><CarsUnder10KPage /></RouteErrorBoundary> },
      { path: 'cars-under-30k', element: <RouteErrorBoundary><CarsUnder30KPage /></RouteErrorBoundary> },
      { path: 'broker-services', element: <RouteErrorBoundary><BrokerServicesPage /></RouteErrorBoundary> },
      { path: 'broker-services/request-a-vehicle', element: <RouteErrorBoundary><RequestAVehiclePage /></RouteErrorBoundary> },
      { path: 'why-use-a-broker', element: <RouteErrorBoundary><WhyUseABrokerPage /></RouteErrorBoundary> },
      { path: 'broker-services/portland', element: <RouteErrorBoundary><BrokerServicesPortlandPage /></RouteErrorBoundary> },
      { path: 'broker-services/seattle', element: <RouteErrorBoundary><BrokerServicesSeattlePage /></RouteErrorBoundary> },
      { path: 'broker-services/bend', element: <RouteErrorBoundary><BrokerServicesBendPage /></RouteErrorBoundary> },
      { path: 'broker-services/eugene', element: <RouteErrorBoundary><BrokerServicesEugenePage /></RouteErrorBoundary> },
      { path: 'broker-services/medford', element: <RouteErrorBoundary><BrokerServicesMedfordPage /></RouteErrorBoundary> },
      { path: 'broker-services/why-broker', element: <RouteErrorBoundary><BrokerServicesWhyBrokerPage /></RouteErrorBoundary> },
      { path: 'community-impact', element: <RouteErrorBoundary><CommunityImpactPage /></RouteErrorBoundary> },
      { path: 'sold-vehicles', element: <RouteErrorBoundary><SoldVehiclesPage /></RouteErrorBoundary> },

      // Service Areas
      { path: 'service-areas', element: <RouteErrorBoundary><ServiceAreasPage /></RouteErrorBoundary> },
      { path: 'service-areas/warrenton', element: <RouteErrorBoundary><WarrentonPage /></RouteErrorBoundary> },
      { path: 'service-areas/astoria', element: <RouteErrorBoundary><AstoriaPage /></RouteErrorBoundary> },
      { path: 'service-areas/seaside', element: <RouteErrorBoundary><SeasidePage /></RouteErrorBoundary> },
      { path: 'service-areas/portland', element: <RouteErrorBoundary><PortlandPage /></RouteErrorBoundary> },
      { path: 'service-areas/gearhart', element: <RouteErrorBoundary><GearhartPage /></RouteErrorBoundary> },
      { path: 'service-areas/ilwaco', element: <RouteErrorBoundary><IlwacoPage /></RouteErrorBoundary> },
      { path: 'service-areas/cannon-beach', element: <RouteErrorBoundary><CannonBeachPage /></RouteErrorBoundary> },
      { path: 'service-areas/tillamook', element: <RouteErrorBoundary><TillamookServicePage /></RouteErrorBoundary> },
      { path: 'service-areas/long-beach-wa', element: <RouteErrorBoundary><LongBeachWAPage /></RouteErrorBoundary> },
      { path: 'service-areas/clatsop-county', element: <RouteErrorBoundary><ClatsopCountyPage /></RouteErrorBoundary> },

      { path: 'faq', element: <RouteErrorBoundary><FaqPage /></RouteErrorBoundary> },
      { path: 'compare', element: <RouteErrorBoundary><ComparePage /></RouteErrorBoundary> },
      { path: 'why-choose-us', element: <Navigate to="/compare" replace /> },
      { path: 'vs/astoria-ford', element: <RouteErrorBoundary><VsAstoriaFordPage /></RouteErrorBoundary> },
      { path: 'vs/lums-toyota', element: <RouteErrorBoundary><VsLumsToyotaPage /></RouteErrorBoundary> },
      { path: 'vs/oceancrest-chevrolet', element: <RouteErrorBoundary><VsOceancrestChevroletPage /></RouteErrorBoundary> },
      { path: 'vs/warrenton-kia', element: <RouteErrorBoundary><VsWarrentonKiaPage /></RouteErrorBoundary> },
      { path: 'sitemap', element: <RouteErrorBoundary><SitemapPage /></RouteErrorBoundary> },
      { path: 'videos', element: <RouteErrorBoundary><VideosPage /></RouteErrorBoundary> },
      { path: 'consignment', element: <RouteErrorBoundary><ConsignmentPage /></RouteErrorBoundary> },
      { path: 'thank-you', element: <RouteErrorBoundary><ThankYouPage /></RouteErrorBoundary> },
      { path: 'thank-you/financing', element: <RouteErrorBoundary><ThankYouFinancingPage /></RouteErrorBoundary> },
      { path: 'thank-you/referral', element: <RouteErrorBoundary><ThankYouReferralPage /></RouteErrorBoundary> },
      { path: 'thank-you/trade-in', element: <RouteErrorBoundary><ThankYouTradeInPage /></RouteErrorBoundary> },
      { path: 'thank-you/dog-tag-referral', element: <RouteErrorBoundary><ThankYouDogTagReferralPage /></RouteErrorBoundary> },
      { path: 'thank-you/check-availability', element: <RouteErrorBoundary><ThankYouCheckAvailabilityPage /></RouteErrorBoundary> },
      { path: 'thank-you/sell-your-car', element: <RouteErrorBoundary><ThankYouSellYourCarPage /></RouteErrorBoundary> },
      { path: 'thank-you/deal-radar', element: <RouteErrorBoundary><ThankYouDealRadarPage /></RouteErrorBoundary> },
      { path: 'thank-you/military', element: <RouteErrorBoundary><ThankYouMilitaryPage /></RouteErrorBoundary> },
      { path: 'used-cars-sw-washington', element: <RouteErrorBoundary><UsedCarsSWWashingtonPage /></RouteErrorBoundary> },
      { path: 'used-cars-astoria-oregon', element: <RouteErrorBoundary><UsedCarsAstoriaOregonPage /></RouteErrorBoundary> },
      { path: 'used-cars-seaside-oregon', element: <RouteErrorBoundary><UsedCarsSeasideOregonPage /></RouteErrorBoundary> },
      { path: 'used-cars-tillamook-oregon', element: <RouteErrorBoundary><UsedCarsTillamookOregonPage /></RouteErrorBoundary> },
      { path: 'used-cars-longview-washington', element: <RouteErrorBoundary><UsedCarsLongviewWashingtonPage /></RouteErrorBoundary> },
      { path: 'used-cars-ilwaco-washington', element: <RouteErrorBoundary><UsedCarsIlwacoWashingtonPage /></RouteErrorBoundary> },
      { path: 'used-cars-portland-oregon', element: <RouteErrorBoundary><UsedCarsPortlandOregonPage /></RouteErrorBoundary> },
      { path: 'used-cars-warrenton-oregon', element: <RouteErrorBoundary><UsedCarsWarrentonOregonPage /></RouteErrorBoundary> },
      { path: 'deal-radar', element: <RouteErrorBoundary><DealRadarPage /></RouteErrorBoundary> },
      { path: 'customer-stories', element: <RouteErrorBoundary><CustomerStoriesPage /></RouteErrorBoundary> },
      { path: 'customer-stories/:slug', element: <RouteErrorBoundary><CustomerStoriesDetailPage /></RouteErrorBoundary> },
      { path: 'how-it-works', element: <RouteErrorBoundary><HowItWorksPage /></RouteErrorBoundary> },
      { path: 'privacy-policy', element: <RouteErrorBoundary><PrivacyPolicyPage /></RouteErrorBoundary> },
      { path: 'terms-of-service', element: <RouteErrorBoundary><TermsOfServicePage /></RouteErrorBoundary> },
      { path: 'referral', element: <RouteErrorBoundary><ReferralPage /></RouteErrorBoundary> },
      { path: 'best-used-cars-under-15k', element: <RouteErrorBoundary><BestUsedCarsUnder15kPage /></RouteErrorBoundary> },
      { path: 'best-used-suvs-under-20k', element: <RouteErrorBoundary><BestUsedSUVsUnder20kPage /></RouteErrorBoundary> },
      { path: 'best-family-cars-oregon', element: <RouteErrorBoundary><BestFamilyCarsOregonPage /></RouteErrorBoundary> },
      { path: 'best-first-time-buyer-cars', element: <RouteErrorBoundary><BestFirstTimeBuyerCarsPage /></RouteErrorBoundary> },
      { path: 'first-time-buyer-guide', element: <RouteErrorBoundary><FirstTimeBuyerGuidePage /></RouteErrorBoundary> },
      { path: 'why-the-dogs', element: <RouteErrorBoundary><WhyTheDogsPage /></RouteErrorBoundary> },
      { path: 'about-the-owner', element: <RouteErrorBoundary><AboutTheOwnerPage /></RouteErrorBoundary> },
      { path: 'military-discount', element: <RouteErrorBoundary><MilitaryDiscountPage /></RouteErrorBoundary> },
      { path: 'rockhound-difference', element: <RouteErrorBoundary><RockhoundDifferencePage /></RouteErrorBoundary> },
      { path: 'fi-survival-checklist', element: <RouteErrorBoundary><FISurvivalChecklistPage /></RouteErrorBoundary> },
      { path: 'compare-vehicles', element: <RouteErrorBoundary><CompareVehiclesPage /></RouteErrorBoundary> },

      // Redirects
      { path: 'oregon-car-buying-guide', element: <Navigate to="/car-buying-guide#oregon-guide" replace /> },
      { path: 'best-suvs-oregon-coast', element: <Navigate to="/best-used-suvs-coastal-weather" replace /> },

      // ── Lazy-loaded content pages (load only when visited) ───────────────
      {
        path: 'car-buying-guide',
        lazy: async () => {
          const { default: Component } = await import('../pages/car-buying-guide/page');
          return { element: <RouteErrorBoundary><Component /></RouteErrorBoundary> };
        },
      },
      {
        path: 'car-buying-guide/:slug',
        lazy: async () => {
          const { default: Component } = await import('../pages/car-buying-guide/article/page');
          return { element: <RouteErrorBoundary><Component /></RouteErrorBoundary> };
        },
      },
      {
        path: 'best-cars-oregon-coast-weather',
        lazy: async () => {
          const { default: Component } = await import('../pages/best-cars-oregon-coast-weather/page');
          return { element: <RouteErrorBoundary><Component /></RouteErrorBoundary> };
        },
      },
      {
        path: 'best-awd-cars-oregon-washington',
        lazy: async () => {
          const { default: Component } = await import('../pages/best-awd-cars-oregon-washington/page');
          return { element: <RouteErrorBoundary><Component /></RouteErrorBoundary> };
        },
      },
      {
        path: 'best-trucks-under-30k-oregon',
        lazy: async () => {
          const { default: Component } = await import('../pages/best-trucks-under-30k-oregon/page');
          return { element: <RouteErrorBoundary><Component /></RouteErrorBoundary> };
        },
      },
      {
        path: 'best-cars-commuting-portland',
        lazy: async () => {
          const { default: Component } = await import('../pages/best-cars-commuting-portland/page');
          return { element: <RouteErrorBoundary><Component /></RouteErrorBoundary> };
        },
      },
      {
        path: 'used-suvs-warrenton-oregon',
        lazy: async () => {
          const { default: Component } = await import('../pages/used-suvs-warrenton-oregon/page');
          return { element: <RouteErrorBoundary><Component /></RouteErrorBoundary> };
        },
      },
      {
        path: 'used-trucks-warrenton-oregon',
        lazy: async () => {
          const { default: Component } = await import('../pages/used-trucks-warrenton-oregon/page');
          return { element: <RouteErrorBoundary><Component /></RouteErrorBoundary> };
        },
      },
      {
        path: 'awd-vehicles-oregon-coast',
        lazy: async () => {
          const { default: Component } = await import('../pages/awd-vehicles-oregon-coast/page');
          return { element: <RouteErrorBoundary><Component /></RouteErrorBoundary> };
        },
      },
      {
        path: 'used-cars-under-20000',
        lazy: async () => {
          const { default: Component } = await import('../pages/used-cars-under-20000/page');
          return { element: <RouteErrorBoundary><Component /></RouteErrorBoundary> };
        },
      },
      {
        path: 'family-suvs-oregon',
        lazy: async () => {
          const { default: Component } = await import('../pages/family-suvs-oregon/page');
          return { element: <RouteErrorBoundary><Component /></RouteErrorBoundary> };
        },
      },
      {
        path: 'best-used-suvs-coastal-weather',
        lazy: async () => {
          const { default: Component } = await import('../pages/best-used-suvs-coastal-weather/page');
          return { element: <RouteErrorBoundary><Component /></RouteErrorBoundary> };
        },
      },
      {
        path: 'toyota-rav4-vs-honda-crv-oregon',
        lazy: async () => {
          const { default: Component } = await import('../pages/toyota-rav4-vs-honda-crv-oregon/page');
          return { element: <RouteErrorBoundary><Component /></RouteErrorBoundary> };
        },
      },
      {
        path: 'best-awd-cars-under-25k',
        lazy: async () => {
          const { default: Component } = await import('../pages/best-awd-cars-under-25k/page');
          return { element: <RouteErrorBoundary><Component /></RouteErrorBoundary> };
        },
      },
      {
        path: 'best-used-cars-oregon-coast',
        lazy: async () => {
          const { default: Component } = await import('../pages/best-used-cars-oregon-coast/page');
          return { element: <RouteErrorBoundary><Component /></RouteErrorBoundary> };
        },
      },
      {
        path: 'oregon-coast-delivery',
        lazy: async () => {
          const { default: Component } = await import('../pages/oregon-coast-delivery/page');
          return { element: <RouteErrorBoundary><Component /></RouteErrorBoundary> };
        },
      },
    ],
  },

  // ── Admin routes ──────────────────────────────────────────────────────────
  {
    path: '/admin',
    element: (
      <AdminAuthProvider>
        <ErrorBoundary
          fallback={
            <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
              <div className="max-w-md w-full text-center">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-red-50 rounded-full">
                  <i className="ri-error-warning-line text-3xl text-red-500"></i>
                </div>
                <h1 className="text-xl font-bold text-gray-900 mb-2">Admin Panel Error</h1>
                <p className="text-gray-600 mb-6">Something went wrong in the admin panel. Reload to try again.</p>
                <button
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line"></i>
                  Reload Admin
                </button>
              </div>
            </div>
          }
        >
          <AdminLayout />
        </ErrorBoundary>
      </AdminAuthProvider>
    ),
    children: [
      { index: true, element: <Navigate to="/admin/dashboard" replace /> },
      { path: 'dashboard', element: <RouteErrorBoundary><AdminDashboardPage /></RouteErrorBoundary> },
      { path: 'inventory', element: <RouteErrorBoundary><AdminInventoryPage /></RouteErrorBoundary> },
      { path: 'reviews', element: <RouteErrorBoundary><AdminReviewsPage /></RouteErrorBoundary> },
      { path: 'reviews/analytics', element: <RouteErrorBoundary><AdminReviewsAnalyticsPage /></RouteErrorBoundary> },
      { path: 'window-sticker', element: <RouteErrorBoundary><AdminWindowStickerPage /></RouteErrorBoundary> },
      { path: 'sticker-analytics', element: <RouteErrorBoundary><AdminStickerAnalyticsPage /></RouteErrorBoundary> },
      { path: 'images', element: <RouteErrorBoundary><AdminImagesPage /></RouteErrorBoundary> },
      { path: 'favicon', element: <RouteErrorBoundary><AdminFaviconPage /></RouteErrorBoundary> },
      { path: 'content', element: <RouteErrorBoundary><AdminContentPage /></RouteErrorBoundary> },
      { path: 'feeds', element: <RouteErrorBoundary><AdminFeedsPage /></RouteErrorBoundary> },
      { path: 'page-preview', element: <RouteErrorBoundary><AdminPagePreviewPage /></RouteErrorBoundary> },
      { path: 'dms', element: <RouteErrorBoundary><AdminDmsPage /></RouteErrorBoundary> },
      { path: 'page-health', element: <RouteErrorBoundary><AdminPageHealthPage /></RouteErrorBoundary> },
      { path: 'referrals', element: <RouteErrorBoundary><AdminReferralsPage /></RouteErrorBoundary> },
      { path: 'financing', element: <RouteErrorBoundary><AdminFinancingPage /></RouteErrorBoundary> },
      { path: 'setup', element: <RouteErrorBoundary><AdminSetupPage /></RouteErrorBoundary> },
      { path: 'security-test', element: <RouteErrorBoundary><AdminSecurityTestPage /></RouteErrorBoundary> },
      {
        path: 'sell-offers',
        lazy: async () => {
          const { default: Component } = await import('../pages/admin/sell-offers/page');
          return { element: <RouteErrorBoundary><Component /></RouteErrorBoundary> };
        },
      },
    ],
  },

  {
    path: '*',
    element: <RouteErrorBoundary><NotFoundPage /></RouteErrorBoundary>,
  },
];

export default routes;